import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { comics, comicCategories } from '@/mocks/comics';

export default function ComicDetail() {
  const { slug } = useParams<{ slug: string }>();
  const comic = comics.find((c) => c.slug === slug);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="w-full px-4 md:px-6 text-center py-20">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-300 mb-4">
              <i className="ri-error-warning-line text-2xl"></i>
            </div>
            <h1 className="font-heading text-2xl text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，该漫画不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const category = comicCategories.find((c) => c.slug === comic.categorySlug);
  const descriptionText =
    comic.description ||
    `${comic.title}是哔咔漫画网收录的${comic.category}题材漫画，作者为${comic.author}，支持免费在线阅读。`;
  const metaDescription = descriptionText.substring(0, 160);
  const similarComics = comics
    .filter((c) => c.id !== comic.id && c.categorySlug === comic.categorySlug)
    .slice(0, 4);

  const chapters = Array.from({ length: Math.min(comic.chapters, 12) }, (_, i) => {
    const num = comic.chapters - i;
    return {
      number: num,
      title: `第${num}话`,
      date: i < 3 ? '今天' : i < 6 ? '昨天' : '2026-06-20',
      free: true,
    };
  });

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: comic.title,
    description: descriptionText,
    author: { '@type': 'Organization', name: comic.author },
    publisher: { '@type': 'Organization', name: '哔咔漫画网', url: 'http://xiaodianba.cn/' },
    url: `http://xiaodianba.cn/comic/${comic.slug}`,
    datePublished: '2020-01-01',
    dateModified: comic.updateDate,
    mainEntityOfPage: { '@type': 'WebPage', '@id': `http://xiaodianba.cn/comic/${comic.slug}` },
    about: { '@type': 'Thing', name: comic.category },
    genre: comic.category,
    keywords: comic.tags.join(', '),
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: comic.rating,
      bestRating: '10',
      reviewCount: '12500',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://xiaodianba.cn/' },
      {
        '@type': 'ListItem',
        position: 2,
        name: category?.name ?? '分类',
        item: `http://xiaodianba.cn/category/${comic.categorySlug}`,
      },
      { '@type': 'ListItem', position: 3, name: comic.title, item: `http://xiaodianba.cn/comic/${comic.slug}` },
    ],
  };

  return (
    <>
      <title>{`${comic.title} - ${comic.author} - 哔咔漫画网`}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={`${comic.title},${comic.category},漫画,在线阅读,哔咔漫画网,${comic.tags.join(',')}`} />
      <link rel="canonical" href={`http://xiaodianba.cn/comic/${comic.slug}`} />
      <meta property="og:title" content={`${comic.title} - 哔咔漫画网`} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={`http://xiaodianba.cn/comic/${comic.slug}`} />
      <meta property="og:site_name" content="哔咔漫画网" />
      <meta property="og:locale" content="zh_CN" />
      <meta property="article:section" content={comic.category} />
      <meta property="article:tag" content={comic.tags.join(',')} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={`${comic.title} - 哔咔漫画网`} />
      <meta name="twitter:description" content={metaDescription} />
      <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="w-full px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: category?.name ?? '分类', path: `/category/${comic.categorySlug}` },
                { label: comic.title },
              ]}
            />

            <article className="mt-6 pb-16">
              <div className="flex flex-col md:flex-row gap-6 md:gap-10">
                <div className="w-full md:w-72 flex-shrink-0">
                  <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-100">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      title={`${comic.title} - ${comic.author} - 哔咔漫画封面`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex gap-3 mt-4">
                    <button className="flex-1 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-book-read-line mr-1"></i>
                      开始阅读
                    </button>
                    <button className="flex-1 py-3 rounded-full bg-background-100 text-foreground-700 font-medium text-sm hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap border border-background-200">
                      <i className="ri-heart-line mr-1"></i>
                      收藏
                    </button>
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                    {comic.title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-foreground-500 mb-4">
                    <span className="flex items-center gap-1">
                      <i className="ri-user-line text-foreground-300"></i>
                      {comic.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-folder-line text-foreground-300"></i>
                      <Link to={`/category/${comic.categorySlug}`} className="hover:text-primary-600 transition-colors">
                        {comic.category}
                      </Link>
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line text-foreground-300"></i>
                      {comic.status}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-star-fill text-accent-400"></i>
                      {comic.rating}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line text-foreground-300"></i>
                      {comic.views}阅读
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-5">
                    {comic.tags.map((tag) => (
                      <span key={tag} className="text-xs bg-secondary-100 text-secondary-700 px-3 py-1 rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="bg-background-100 rounded-xl p-4 md:p-5 mb-6">
                    <h2 className="font-heading text-lg font-bold text-foreground-900 mb-2">简介</h2>
                    <p className="text-sm text-foreground-600 leading-relaxed">{descriptionText}</p>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="font-heading text-lg font-bold text-foreground-900">章节列表</h2>
                      <span className="text-sm text-foreground-500">共 {comic.chapters} 话</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                      {chapters.map((ch) => (
                        <button
                          key={ch.number}
                          className="text-left px-4 py-3 rounded-lg bg-background-100 hover:bg-background-200 transition-colors text-sm text-foreground-700 cursor-pointer"
                        >
                          <div className="font-medium text-foreground-800">{ch.title}</div>
                          <div className="text-xs text-foreground-400 mt-0.5">{ch.date}</div>
                        </button>
                      ))}
                    </div>
                    {comic.chapters > 12 && (
                      <button className="w-full mt-3 py-2.5 rounded-lg bg-background-100 text-sm text-foreground-500 hover:text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer">
                        查看全部 {comic.chapters} 话
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {similarComics.length > 0 && (
                <div className="mt-12">
                  <h2 className="font-heading text-xl font-bold text-foreground-900 mb-5">同类推荐</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {similarComics.map((similar) => (
                      <Link key={similar.id} to={`/comic/${similar.slug}`} className="group">
                        <div className="relative aspect-[3/4] rounded-xl overflow-hidden mb-2 bg-background-100">
                          <img
                            src={similar.cover}
                            alt={similar.title}
                            title={`${similar.title} - 同类漫画推荐`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <h3 className="text-sm font-bold text-foreground-900 truncate group-hover:text-primary-600 transition-colors">
                          {similar.title}
                        </h3>
                        <p className="text-xs text-foreground-500 mt-0.5 truncate">{similar.author}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </article>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}
